/**
 * MembershipPolicy — public policy page covering:
 *   • Membership (weekly recurring) auto-renew rules
 *   • Pack expiry (member packs = 90d, non-member = no expiry)
 *   • Session preservation options
 *   • The 2:1 fallback when nothing is preserved
 *   • Cancellation flow + what happens to unused sessions
 *
 * Sachin 2026-03-05: "Take the prices off the website." — every explicit
 * dollar amount is stripped from this page. Parents/leads are pointed at
 * their coach for the actual price sheet. POLICY_VERSION was bumped so
 * previously-acknowledged parents re-acknowledge the updated language.
 *
 * The page version (POLICY_VERSION) is bumped whenever the actual policy
 * changes. Authenticated parents see an acknowledgement checkbox at the
 * bottom — once acknowledged, the version is stamped on their user record.
 * If a future bump introduces a newer version, parents will be prompted
 * to re-acknowledge on their next signed-in visit (handled by MemberAlertsStrip).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft, AlertTriangle, Repeat, X, Wallet, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export const POLICY_VERSION = "1.2";  // 2026-03-05: dollar amounts scrubbed from public policy page

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MembershipPolicy() {
    const { user } = useAuth();
    const [acked, setAcked] = useState(null);  // null=loading, string version=acked, ""=not acked
    const [submitting, setSubmitting] = useState(false);
    const [checkbox, setCheckbox] = useState(false);
    const isParent = user && !["coach", "owner"].includes(user.role);

    useEffect(() => {
        if (!user) {
            setAcked("");
            return;
        }
        // Check current acknowledgement status
        let cancelled = false;
        (async () => {
            try {
                const r = await axios.get(`${API}/me/policy-acknowledgement`, { withCredentials: true });
                if (!cancelled) {
                    setAcked(r.data?.version || "");
                    setCheckbox(r.data?.version === POLICY_VERSION);
                }
            } catch {
                if (!cancelled) setAcked("");
            }
        })();
        return () => { cancelled = true; };
    }, [user]);

    const submitAck = async () => {
        if (!checkbox) return;
        setSubmitting(true);
        try {
            await axios.post(
                `${API}/me/policy-acknowledgement`,
                { version: POLICY_VERSION },
                { withCredentials: true },
            );
            setAcked(POLICY_VERSION);
            toast.success("Got it — policy acknowledged.");
        } catch (e) {
            toast.error("Couldn't save — try again", { description: e?.response?.data?.detail });
        } finally {
            setSubmitting(false);
        }
    };

    const needsAck = isParent && acked !== POLICY_VERSION;
    const isOutOfDate = isParent && acked && acked !== POLICY_VERSION;

    return (
        <div data-testid="membership-policy" className="min-h-screen bg-black text-white pt-28 pb-16">
            <div className="max-w-3xl mx-auto px-4">
                <Link to="/dashboard" className="inline-flex items-center gap-1 text-zinc-400 hover:text-white text-sm mb-6">
                    <ArrowLeft className="w-4 h-4" /> Back to dashboard
                </Link>

                <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-3">Membership policy</div>
                <h1 className="font-display text-4xl md:text-5xl tracking-wider mb-3">How it works.</h1>
                <p className="text-sm text-zinc-400 mb-2">Effective {new Date().toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })} · Version {POLICY_VERSION}</p>
                <p className="text-base text-zinc-200 mb-10 leading-relaxed">
                    Clear and simple — we want zero surprises about pricing, expiry, and cancellation.
                    If anything below is unclear, text the gym or message us anytime.
                </p>

                {/* SECTION 0 — PRICE SHEET
                    Sachin 2026-03-05: "Take the prices off the website."
                    The full public price sheet is gone. Parents/leads see
                    a placeholder pointing them at their coach for the
                    current rates (sent after the assessment). */}
                <Section icon={<Wallet className="w-5 h-5 text-brand-red" />} title="Pricing">
                    <p>Pricing is handed off in person after your assessment — your coach sends the current price sheet directly, so you get options that match what your athlete actually needs.</p>
                    <p className="mt-3 text-[13px] text-zinc-400">Reach out anytime if you&apos;d like to talk numbers before your first visit — we&apos;ll walk you through it.</p>
                    <p className="mt-4 text-[12px] text-amber-200 bg-amber-900/15 border border-amber-700/30 rounded-lg px-3 py-2 inline-block">
                        <strong>Cancellation policy:</strong> any unused member sessions convert at 2:1 when you cancel
                        (e.g. 4 left → 2 kept, 2 forfeited). Full details below.
                    </p>
                </Section>

                {/* SECTION 1 — Membership */}
                <Section icon={<Repeat className="w-5 h-5 text-brand-red" />} title="Recurring membership">
                    <p>The Weekly Membership is <strong className="text-white">auto-billed every Monday</strong>. It&apos;s truly week-to-week — <strong className="text-white">cancel anytime, no commitment.</strong></p>
                    <p>Active membership unlocks <strong className="text-white">Member pack pricing</strong> — meaningfully cheaper per session than non-member packs. Your coach shares the exact numbers after your assessment.</p>
                </Section>

                {/* SECTION 2 — Pack expiry */}
                <Section icon={<Clock className="w-5 h-5 text-brand-red" />} title="Session-pack expiry">
                    <div className="rounded-lg border border-zinc-800 bg-zinc-950/40 p-4 mb-3">
                        <table className="w-full text-sm">
                            <thead className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                                <tr><th className="text-left pb-2">Pack type</th><th className="text-right pb-2">Expires?</th></tr>
                            </thead>
                            <tbody className="text-zinc-200">
                                <tr className="border-t border-zinc-800"><td className="py-2">Member · Auto pack</td><td className="text-right">90 days</td></tr>
                                <tr className="border-t border-zinc-800"><td className="py-2">Member · Manual pack</td><td className="text-right">90 days</td></tr>
                                <tr className="border-t border-zinc-800"><td className="py-2">Non-Member pack</td><td className="text-right text-emerald-400">Never expires</td></tr>
                            </tbody>
                        </table>
                    </div>
                    <p>We send you a heads-up <strong className="text-white">14 days</strong> and <strong className="text-white">7 days</strong> before any member pack expires — you'll have plenty of time to decide what to do.</p>
                </Section>

                {/* SECTION 3 — Preservation */}
                <Section icon={<Wallet className="w-5 h-5 text-brand-red" />} title="Got unused sessions when a pack expires?">
                    <p>Three options. All handled by your coach — just reply to the heads-up email or message us.</p>
                    <div className="space-y-3 mt-4">
                        <PolicyOption letter="A" title="Roll into a new pack (small preserve fee)" body="Your unused sessions stack on top of a new pack. Smallest fee, fresh 90-day clock on the combined balance. Best value when you'd buy a new pack anyway. Your coach shares the exact fee when we chat." />
                        <PolicyOption letter="B" title="Preserve standalone (per-session fee, no expiry)" body="Sessions become evergreen — usable forever, as long as your membership stays active. Great if you don't need a new pack right now. Your coach shares the exact fee when we chat." />
                        <PolicyOption letter="C" title="Do nothing — 2:1 courtesy ratio" body="If you don't reply, we revive half your unused sessions as a courtesy (we round up). The other half forfeit. No fee, but you lose half." muted />
                    </div>
                </Section>

                {/* SECTION 4 — Cancellation */}
                <Section icon={<X className="w-5 h-5 text-brand-red" />} title="Cancelling your membership">
                    <p>You can cancel anytime — two paths:</p>
                    <ul className="list-disc pl-5 space-y-2 text-zinc-300">
                        <li><strong className="text-white">In-app</strong>: the "Cancel membership" button on your dashboard. We process it immediately and email a confirmation.</li>
                        <li><strong className="text-white">Stripe billing portal</strong>: cancel at period end through Stripe's hosted UI (lets you finish out the week you've already paid for).</li>
                    </ul>
                    <p className="mt-3">When you cancel, <strong className="text-white">any unused session credits convert at 2:1</strong> — we round up. Example: <em>4 sessions left → 2 kept, 2 forfeited</em>. The kept sessions stay on your account so you can use them whenever — they just stop being member-priced.</p>
                </Section>

                {/* SECTION 5 — Refunds */}
                <Section icon={<AlertTriangle className="w-5 h-5 text-brand-red" />} title="Refunds">
                    <p>We don't issue cash refunds on session packs or membership fees — but we'll always work with you on schedule changes, plan swaps, or pausing your membership. Just talk to us.</p>
                </Section>

                {/* Acknowledgement — only for signed-in parents */}
                {isParent && (
                    <div className="mt-12 border border-brand-red/40 bg-brand-red/5 rounded-2xl p-6">
                        {acked === POLICY_VERSION ? (
                            <div data-testid="policy-acked" className="flex items-center gap-3 text-emerald-400">
                                <CheckCircle2 className="w-5 h-5" />
                                <span className="text-sm">You acknowledged this policy on {new Date().toLocaleDateString()}. Thank you.</span>
                            </div>
                        ) : (
                            <>
                                {isOutOfDate && (
                                    <div className="text-xs text-amber-300 mb-3 inline-flex items-center gap-1">
                                        <AlertTriangle className="w-3.5 h-3.5" /> Our policy was updated. Please review and re-acknowledge.
                                    </div>
                                )}
                                <label className="flex items-start gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        data-testid="policy-checkbox"
                                        checked={checkbox}
                                        onChange={(e) => setCheckbox(e.target.checked)}
                                        className="mt-1 w-5 h-5 accent-brand-red"
                                    />
                                    <span className="text-sm text-zinc-200 leading-relaxed">
                                        I've read and understand the MaxSC membership policy — including
                                        expiry, preservation options, and the 2:1 courtesy ratio if a pack
                                        expires without action.
                                    </span>
                                </label>
                                <button
                                    type="button"
                                    data-testid="policy-ack-submit"
                                    onClick={submitAck}
                                    disabled={!checkbox || submitting}
                                    className="mt-4 w-full md:w-auto px-6 py-3 rounded-xl bg-brand-red text-white font-medium hover:bg-brand-red/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                >
                                    {submitting ? "Saving…" : "I understand — acknowledge"}
                                </button>
                            </>
                        )}
                    </div>
                )}

                {needsAck === false && acked && (
                    <p className="text-[11px] text-zinc-500 mt-6 text-center">
                        Policy v{POLICY_VERSION}. Last updated June 2026.
                    </p>
                )}
            </div>
        </div>
    );
}

function Section({ icon, title, children }) {
    return (
        <section className="mb-8 border-t border-zinc-900 pt-6">
            <div className="flex items-center gap-2 mb-3">
                {icon}
                <h2 className="font-display text-2xl tracking-wider">{title}</h2>
            </div>
            <div className="text-sm text-zinc-300 leading-relaxed space-y-2">
                {children}
            </div>
        </section>
    );
}

function PolicyOption({ letter, title, body, muted }) {
    return (
        <div className={`flex items-start gap-3 p-3 rounded-lg border ${muted ? "border-zinc-800 bg-zinc-950/30" : "border-zinc-800 bg-zinc-950/50"}`}>
            <span className={`shrink-0 w-7 h-7 rounded-full inline-flex items-center justify-center text-sm font-bold ${muted ? "bg-zinc-800 text-zinc-300" : "bg-brand-red text-white"}`}>
                {letter}
            </span>
            <div>
                <div className="text-white text-sm font-semibold">{title}</div>
                <div className="text-[13px] text-zinc-400 mt-1 leading-relaxed">{body}</div>
            </div>
        </div>
    );
}
